import React, { useState, useEffect, useRef } from "react";
import { 
  Wrench, Zap, Sliders, ShieldAlert, Network, Hammer, Activity, Layout, Eye, Cpu
} from "lucide-react";

export const SpecialtiesGrid = () => {
  const [hoveredIndex, setHoveredCard] = useState(null);
  const [activeCount, setActiveCount] = useState(-1);
  const containerRef = useRef(null);

  const specialties = [
    {
      title: "Critical Facility Technicians",
      icon: Wrench,
      description: "Direct control, diagnostic operations, electrical/mechanical system maintenance, and predictive failure mitigation in multi-MW tech facilities."
    },
    {
      title: "Electrical Engineers",
      icon: Zap,
      description: "High/medium voltage transmission architectures, UPS power redundancy design, and static transfer switch topologies."
    },
    {
      title: "Mechanical Engineers",
      icon: Sliders,
      description: "Thermodynamics optimization, chillers deployment, liquid manifold designs, and hot/cold aisle thermal containment systems."
    },
    {
      title: "HVAC Specialists",
      icon: Cpu,
      description: "Direct-expansion cooling units, CRAH and CRAC architectures, variable-frequency drives, and cooling tower commissioning."
    },
    {
      title: "Commissioning Managers",
      icon: Activity,
      description: "Comprehensive operational Level 1 to 5 commissioning scripts execution, site design criteria validation, and ASHRAE certifications."
    },
    {
      title: "Controls Engineers",
      icon: Layout,
      description: "BMS/EPMS programming, SCADA monitoring loops implementation, automated transfer scheme logics, and hardware integration."
    },
    {
      title: "Network Engineers",
      icon: Network,
      description: "Dense fiber-optic transport alignments, BGP transit systems management, and zero-packet-loss high-speed network topologies."
    },
    {
      title: "Operations Managers",
      icon: Eye,
      description: "24/7 technical operations coordination, physical facilities security compliance, and preventative maintenance strategies."
    },
    {
      title: "Construction Managers",
      icon: Hammer,
      description: "Civil and infrastructural site build direction, subcontractor dispatch, structural steel frames, and critical timeline management."
    },
    {
      title: "Security & Compliance",
      icon: ShieldAlert,
      description: "Physical and network compliance auditing (SOC 2, ISO 27001, PCI-DSS), access systems, and zero-trust protocol audits."
    }
  ];

  // Icons light up progressively tied to scroll position (like the process timeline)
  useEffect(() => {
    const handleScroll = () => {
      if (!containerRef.current) return;
      const rect = containerRef.current.getBoundingClientRect();
      const viewportHeight = window.innerHeight;
      if (rect.top < viewportHeight * 0.8 && rect.bottom > 0) {
        const progress = Math.max(0, Math.min(1, (viewportHeight * 0.5 - rect.top) / rect.height));
        setActiveCount(Math.min(specialties.length - 1, Math.floor(progress * specialties.length)));
      }
    };
    handleScroll();
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <section 
      id="solutions"
      ref={containerRef}
      data-testid="specialties-section"
      className="relative z-10 max-w-7xl mx-auto px-6 md:px-12 py-14 md:py-16"
    >
      <div className="text-center mb-16">
        <span className="text-xs font-mono uppercase tracking-[0.2em] text-[#65FFB6]">
          DISPATCH VERTICALS
        </span>
        <h2 className="text-3xl md:text-5xl font-outfit font-bold tracking-tight text-white mt-3">
          Recruitment Specialties
        </h2>
        <p className="text-slate-400 font-manrope text-base max-w-2xl mx-auto mt-4">
          Securing elite human capital to power the world&apos;s most sophisticated and demanding infrastructure networks.
        </p>
      </div>

      <div 
        data-testid="specialties-grid"
        className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6"
      >
        {specialties.map((spec, idx) => {
          const IconComponent = spec.icon;
          const isHovered = hoveredIndex === idx;
          const isLit = isHovered || idx <= activeCount;

          return (
            <div
              key={idx}
              data-testid={`specialty-card-${idx}`}
              onMouseEnter={() => setHoveredCard(idx)}
              onMouseLeave={() => setHoveredCard(null)}
              className={`glass-panel p-6 rounded-2xl border border-white/10 flex flex-col items-start gap-4 transition-all duration-300 bg-black/30 cursor-pointer ${
                isHovered 
                  ? "border-[#65FFB6]/40 shadow-[0_0_25px_rgba(101,255,182,0.12)] -translate-y-1.5" 
                  : "hover:border-white/20"
              }`}
            >
              <div className={`w-11 h-11 rounded-xl flex items-center justify-center border transition-all duration-500 ${
                isLit 
                  ? "bg-[#0B1F1A] border-[#65FFB6]/50 text-[#65FFB6] shadow-[0_0_15px_rgba(101,255,182,0.35)]" 
                  : "bg-white/5 border-white/10 text-slate-500"
              }`}>
                <IconComponent className="w-5 h-5" />
              </div>

              <div>
                <h3 className={`text-lg font-outfit font-semibold text-white transition-colors duration-200 ${
                  isHovered ? "text-[#65FFB6]" : ""
                }`}>
                  {spec.title}
                </h3>
                <p className="text-sm font-manrope text-slate-400 mt-2 leading-relaxed">
                  {spec.description}
                </p>
              </div>
            </div>
          );
        })}
      </div>
    </section>
  );
};
